import { useEffect, useState, type FormEvent } from 'react';
import { Skeleton } from '../../components/Skeleton';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import {
  COURSE_LABELS,
  createRecipeRequestSchema,
  CUISINE_LABELS,
  type RecipeDetail,
} from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { Button } from '../../components/Button';
import { Field } from '../../components/Field';
import { Section } from '../../components/Section';
import { ChoiceChipPicker } from '../../components/recipes/ChoiceChipPicker';
import { IngredientRowsEditor } from '../../components/recipes/IngredientRowsEditor';
import { StepRowsEditor } from '../../components/recipes/StepRowsEditor';
import { TagChipInput } from '../../components/recipes/TagChipInput';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { messageForError } from '../../lib/errors';
import {
  describeIssues,
  emptyForm,
  formFromDetail,
  toRecipeRequest,
  type RecipeFormState,
} from './recipe-form';
import { recipeKeys } from './queries';
import { COURSE_GROUPS, CUISINE_GROUPS } from './taxonomy';

export function RecipeFormPage({ mode }: { mode: 'create' | 'edit' }) {
  const { id } = useParams<{ id: string }>();
  const { auth } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const defaultServings =
    auth.status === 'authed' ? (auth.household?.defaultServings ?? null) : null;

  const [form, setForm] = useState<RecipeFormState>(() => emptyForm(defaultServings));
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [issues, setIssues] = useState<string[]>([]);

  const existing = useQuery({
    queryKey: recipeKeys.detail(id ?? ''),
    queryFn: () => api.recipes.get(id!),
    enabled: mode === 'edit' && Boolean(id),
  });
  const tags = useQuery({ queryKey: recipeKeys.tags, queryFn: () => api.recipes.tags() });

  // Seed the editor once the recipe arrives; later refetches must not stomp on edits.
  useEffect(() => {
    if (mode !== 'edit' || !existing.data) return;
    if (loadedId === existing.data.id) return;
    setForm(formFromDetail(existing.data));
    setLoadedId(existing.data.id);
  }, [mode, existing.data, loadedId]);

  const save = useMutation({
    mutationFn: async (body: ReturnType<typeof toRecipeRequest>): Promise<RecipeDetail> =>
      mode === 'edit' ? api.recipes.update(id!, body) : api.recipes.create(body),
    onSuccess: async (recipe) => {
      qc.setQueryData(recipeKeys.detail(recipe.id), recipe);
      await qc.invalidateQueries({ queryKey: recipeKeys.all });
      void navigate(`/recipes/${recipe.id}`, { replace: true });
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const body = toRecipeRequest(form);
    const parsed = createRecipeRequestSchema.safeParse(body);
    if (!parsed.success) {
      setIssues(describeIssues(parsed.error));
      return;
    }
    setIssues([]);
    save.mutate(body);
  }

  if (mode === 'edit' && existing.isPending) {
    return (
      <AppShell title="Edit recipe">
        <Skeleton lines={4} />
      </AppShell>
    );
  }
  if (mode === 'edit' && existing.isError) {
    return (
      <AppShell title="Edit recipe">
        <Alert tone="error">{messageForError(existing.error)}</Alert>
        <Link to="/recipes" className="mt-3 inline-block text-sm text-brand underline">
          Back to recipes
        </Link>
      </AppShell>
    );
  }
  if (mode === 'edit' && existing.data && !existing.data.own) {
    return (
      <AppShell title="Edit recipe">
        <Alert tone="warning">
          This recipe belongs to another household. Copy it to your recipes first, then edit the
          copy.
        </Alert>
        <Link to={`/recipes/${id}`} className="mt-3 inline-block text-sm text-brand underline">
          Back to the recipe
        </Link>
      </AppShell>
    );
  }

  return (
    <AppShell title={mode === 'edit' ? 'Edit recipe' : 'New recipe'}>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Section accent="orange" title="Basics">
          <Field
            id="title"
            label="Title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
            maxLength={200}
          />
          <div className="flex flex-col gap-1">
            <label htmlFor="description" className="text-sm font-medium text-ink">
              Description
            </label>
            <textarea
              id="description"
              rows={3}
              maxLength={1000}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="rounded-lg border border-line bg-surface p-3 text-base text-ink placeholder:text-muted"
            />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field
              id="servings"
              label="Serves"
              type="number"
              inputMode="numeric"
              min={1}
              max={200}
              value={form.servings}
              onChange={(e) => setForm({ ...form, servings: e.target.value })}
            />
            <Field
              id="prepMin"
              label="Prep (min)"
              type="number"
              inputMode="numeric"
              min={0}
              max={10080}
              value={form.prepMin}
              onChange={(e) => setForm({ ...form, prepMin: e.target.value })}
            />
            <Field
              id="cookMin"
              label="Cook (min)"
              type="number"
              inputMode="numeric"
              min={0}
              max={10080}
              value={form.cookMin}
              onChange={(e) => setForm({ ...form, cookMin: e.target.value })}
            />
            <Field
              id="restMin"
              label="Rest before serving (min)"
              type="number"
              inputMode="numeric"
              min={0}
              max={1440}
              value={form.restMin}
              onChange={(e) => setForm({ ...form, restMin: e.target.value })}
            />
          </div>
          <TagChipInput
            value={form.tags}
            onChange={(next) => setForm({ ...form, tags: next })}
            suggestions={(tags.data?.tags ?? []).map((t) => t.tag)}
          />
          <label className="flex min-h-11 items-center gap-3 text-sm text-ink">
            <input
              type="checkbox"
              checked={form.visibility === 'public'}
              onChange={(e) =>
                setForm({ ...form, visibility: e.target.checked ? 'public' : 'private' })
              }
              className="size-5 accent-[var(--color-brand)]"
            />
            Share this recipe with other FewerBags households
          </label>
        </Section>

        <Section
          accent="orange"
          title="Cuisine & course"
          id="cuisine-course"
          description="Used for filtering, rankings and the week planner. Untagged recipes count as entrées."
        >
          <ChoiceChipPicker
            id="courses"
            label="Course"
            hint="What part of the meal is it?"
            groups={COURSE_GROUPS}
            labels={COURSE_LABELS}
            value={form.courses}
            onChange={(next) => setForm({ ...form, courses: next })}
          />
          <ChoiceChipPicker
            id="cuisines"
            label="Cuisine"
            groups={CUISINE_GROUPS}
            labels={CUISINE_LABELS}
            value={form.cuisines}
            onChange={(next) => setForm({ ...form, cuisines: next })}
          />
        </Section>

        <Section
          accent="orange"
          title="Ingredients"
          description="Paste a whole list, or add rows one at a time."
        >
          <IngredientRowsEditor
            rows={form.ingredients}
            onChange={(next) => setForm({ ...form, ingredients: next })}
          />
        </Section>

        <Section
          accent="orange"
          title="Steps"
          description="Durations and equipment feed the cooking timeline later on."
        >
          <StepRowsEditor
            rows={form.steps}
            ingredientRows={form.ingredients}
            onChange={(next) => setForm({ ...form, steps: next })}
          />
        </Section>

        {issues.length > 0 ? (
          <Alert tone="error">
            <p className="font-semibold">Fix these before saving:</p>
            <ul className="mt-1 list-disc pl-5">
              {issues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          </Alert>
        ) : null}
        {save.isError ? <Alert tone="error">{messageForError(save.error)}</Alert> : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Create recipe'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void navigate(mode === 'edit' && id ? `/recipes/${id}` : '/recipes')}
          >
            Cancel
          </Button>
        </div>
      </form>
    </AppShell>
  );
}
